/**
 * OptionMenu — a dropdown menu to list a set of related actions
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices-1.2/#menubutton
 */
import React, {
  createContext,
  RefObject,
  ReactElement,
  cloneElement,
  Component,
  createRef,
  ReactNode,
  CSSProperties,
  FocusEvent,
} from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react";
import { action, observable, computed } from "mobx";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import observeRect from "@reach/observe-rect";
import classNames from "classnames";
import { AnimationDuration } from "../../../styles/Animations";
import { randomString } from "../../../utils";
import forceReflow from "../../../utils/forceReflow";
import getPortalRoot from "../../../utils/getPortalRoot";
import Panel from "../panel/Panel/Panel";
import getOverlayPosition, { OverlayPositionProps } from "../../../utils/getOverlayPosition";
import { OptionMenuItemsContext } from "./OptionMenuItemsContext";

type OptionMenuSize = "small" | "medium" | "large";

enum Keys {
  Escape = "Escape",
  ArrowDown = "ArrowDown",
  ArrowUp = "ArrowUp",
  Enter = "Enter",
  Space = " ",
}

enum FocusPosition {
  First = "First",
  Last = "Last",
}

interface IOptionMenuProps {
  /**
   * Manually control the open state of the menu. By default this is
   * handled internally, but setting isOpen overrides the internal state.
   * When setting isOpen make sure to pass a close function to onClose
   */
  isOpen?: boolean;
  /**
   * Used to call a close event for the outer click;
   * escape key; and blur handlers when controlled with isOpen
   */
  onClose?: () => void;
  /**
   * A button to trigger the OptionMenu. By default the menu will set
   * its position based on this button
   */
  button: ReactElement;
  /**
   * Set to false to disable closing on outer click and blur
   */
  closeOnOuterClick?: boolean;
  size?: OptionMenuSize;
  className?: string;
  style?: CSSProperties;
}

export interface IOptionMenuContext {
  close: () => void;
  buttonRef: RefObject<HTMLElement>;
}

// Use context to allow children to close the menu
export const OptionMenuContext = createContext({} as IOptionMenuContext);
export const OptionMenuClassName = "option-menu";

@observer
class OptionMenu extends Component<IOptionMenuProps> {
  public static defaultProps = {
    closeOnOuterClick: true,
    size: "medium",
  };

  private internalButtonRef: RefObject<HTMLElement> = createRef();
  private menuId = `option-menu-${randomString()}`;
  private portalRoot = getPortalRoot("option-menu-root");

  @observable.ref
  private internalIsOpen = false;

  @observable.ref
  private initialFocus: FocusPosition | null = null;

  @computed
  private get isOpen(): boolean {
    return this.isControlled ? !!this.props.isOpen : this.internalIsOpen;
  }

  @computed
  private get isControlled(): boolean {
    // The component considers itself controlled if the isOpen prop is passes
    // This causes it to use the external isOpen state instead of the internal state
    return this.props.isOpen !== undefined;
  }

  @computed
  private get buttonRef(): RefObject<HTMLElement> {
    return this.props.button.props.ref || this.internalButtonRef;
  }

  @action
  private closeMenu = (): void => {
    this.internalIsOpen = false;
    this.props.onClose?.();
  };

  @action
  private toggleMenu = (): void => {
    this.internalIsOpen = !this.internalIsOpen;

    if (!this.isOpen && !this.isControlled) {
      this.props.onClose?.();
    }
  };

  @action
  private handleButtonClick = (event: React.MouseEvent<HTMLElement>): void => {
    this.props.button.props.onClick?.(event);
    this.toggleMenu();
  };

  private handleButtonMouseDown = (event: FocusEvent<HTMLElement>): void => {
    // Prevent onMouseDown triggering a blur event
    event.preventDefault();
    this.props.button.props.onMouseDown?.(event);
    this.initialFocus = null;
  };

  @action
  private handleButtonKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key === Keys.ArrowDown || event.key === Keys.ArrowUp) {
      // Prevent scrolling
      event.preventDefault();
      this.toggleMenu();
      this.props.button.props.onClick?.(event);
    }

    // Use arrow keys to open the menu and focus the first / last item
    if (event.key === Keys.ArrowDown) {
      this.initialFocus = FocusPosition.First;
    } else if (event.key === Keys.ArrowUp) {
      this.initialFocus = FocusPosition.Last;
    } else if (event.key === Keys.Enter || event.key === Keys.Space) {
      // Focus the first item when the menu is triggered by enter or space
      this.initialFocus = FocusPosition.First;
    }
  };

  public render(): ReactNode {
    const { button, children, className, style, size, closeOnOuterClick } = this.props;
    return (
      <OptionMenuContext.Provider value={{ close: this.closeMenu, buttonRef: this.buttonRef }}>
        {/* Apply refs and accessibility attributes to the button element */}
        {cloneElement(button, {
          ["aria-haspopup"]: true,
          ["aria-expanded"]: this.isOpen,
          ["aria-controls"]: this.menuId,
          ["aria-owns"]: this.menuId,
          ref: this.buttonRef,
          onMouseDown: this.handleButtonMouseDown,
          onKeyDown: this.handleButtonKeyDown,
          onClick: this.handleButtonClick,
        })}
        <Transition
          mountOnEnter
          unmountOnExit
          onEnter={forceReflow}
          in={this.isOpen}
          timeout={AnimationDuration.Medium}
        >
          {(status): ReactElement =>
            createPortal(
              <OptionMenuList
                id={this.menuId}
                status={status}
                isOpen={!!this.isOpen}
                buttonRef={this.buttonRef}
                closeMenu={this.closeMenu}
                closeOnOuterClick={!!closeOnOuterClick}
                size={size}
                className={className}
                initialFocus={this.initialFocus ?? undefined}
                style={style}
              >
                {children}
              </OptionMenuList>,
              this.portalRoot as Element,
            )
          }
        </Transition>
      </OptionMenuContext.Provider>
    );
  }
}

interface IOptionMenuListProps {
  id: string;
  status: TransitionStatus;
  buttonRef: RefObject<HTMLElement>;
  closeOnOuterClick: boolean;
  size?: OptionMenuSize;
  isOpen: boolean;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  initialFocus?: FocusPosition;
  closeMenu: () => void;
}

@observer
class OptionMenuList extends Component<IOptionMenuListProps> {
  private timeoutId: number | undefined;
  private menuItems: RefObject<HTMLElement>[] = [];
  private menuRef: RefObject<HTMLDivElement> = createRef();
  private rectObserver: { observe: () => void; unobserve: () => void } | undefined;
  private intersectionObserver?: IntersectionObserver;

  @observable
  private popoverPosition: OverlayPositionProps | undefined;

  @observable
  private menuItemFocusIndex = 0;

  @observable
  private hasClickedOutside = false;

  public componentDidMount(): void {
    const focusOptions = { preventScroll: true };

    // Shift focus to the menu
    if (this.props.initialFocus === FocusPosition.First) {
      this.menuItems[0]?.current?.focus(focusOptions);
    } else if (this.props.initialFocus === FocusPosition.Last) {
      this.menuItems[this.menuItems.length - 1]?.current?.focus(focusOptions);
    } else {
      this.menuRef.current?.focus(focusOptions);
    }

    if (this.props.buttonRef.current) {
      this.rectObserver = observeRect(this.props.buttonRef.current, this.handleRectObserver);
      this.intersectionObserver = new IntersectionObserver(this.handleIntersectionObserver);
      this.intersectionObserver.observe(this.props.buttonRef.current);
      this.rectObserver.observe();
    }

    document.addEventListener("mousedown", this.handleOuterClick);
    document.addEventListener("keydown", this.handleEscKey);
  }

  public componentWillUnmount(): void {
    this.rectObserver?.unobserve();
    this.intersectionObserver?.disconnect();
    document.removeEventListener("mousedown", this.handleOuterClick);
    document.removeEventListener("keydown", this.handleEscKey);
  }

  private handleEscKey = (event: KeyboardEvent): void => {
    if (event.key === Keys.Escape && this.props.isOpen) {
      this.props.closeMenu();
      this.props.buttonRef.current?.focus({ preventScroll: true });
    }
  };

  @action
  private handleRectObserver = (buttonRect: DOMRect): void => {
    const menuRect = this.menuRef.current?.getBoundingClientRect();

    this.popoverPosition = getOverlayPosition({
      targetRect: buttonRect,
      overlayRect: menuRect,
      position: "bottom",
      offset: 4,
      outerMargin: 4,
    });
  };

  @action
  private handleIntersectionObserver = ([entry]: IntersectionObserverEntry[]): void => {
    if (!entry.isIntersecting) {
      this.props.closeMenu();
    }
  };

  @action
  private handleOuterClick = (event: MouseEvent): void => {
    if (!this.props.isOpen || !this.props.closeOnOuterClick) return;

    const targetNode = event.target as Node;
    const isInner = this.menuRef.current?.contains(targetNode);
    const isButton = this.props.buttonRef.current === targetNode;
    const isButtonChild = this.props.buttonRef.current?.contains(targetNode);

    if (!isInner && !isButton && !isButtonChild) {
      this.hasClickedOutside = true;
      this.props.closeMenu();
      clearTimeout(this.timeoutId);
    }
  };

  // Create a timeout to close the menu on blur so we can check if
  // the next focused item is within the menu
  private handleBlur = (): void => {
    if (!this.props.closeOnOuterClick) return;

    this.timeoutId = window.setTimeout(() => {
      this.props.closeMenu();

      if (!this.hasClickedOutside) {
        this.props.buttonRef.current?.focus({ preventScroll: true });
      }
    });
  };

  // Because blur triggers when tabbing between items, we need to
  // cancel the close if the next focused item is inside the menu.
  @action
  private handleFocus = (event: FocusEvent<HTMLElement>): void => {
    const focusedItem = this.menuItems.find((item) => item.current === event.target);
    const focusIndex = focusedItem != null ? this.menuItems.indexOf(focusedItem) : -1;
    this.menuItemFocusIndex = focusIndex;
    clearTimeout(this.timeoutId);
  };

  // Keyboard interaction for arrow up / down to move focus between menu items
  @action
  private handleKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    const prevIndex = (this.menuItemFocusIndex - 1 + this.menuItems.length) % this.menuItems.length;
    const nextIndex = (this.menuItemFocusIndex + 1) % this.menuItems.length;

    if (event.key === Keys.ArrowUp) {
      // Prevent scrolling
      event.preventDefault();
      this.menuItemFocusIndex = prevIndex;
      this.menuItems[prevIndex].current?.focus();
    } else if (event.key === Keys.ArrowDown) {
      // Prevent scrolling
      event.preventDefault();
      this.menuItemFocusIndex = nextIndex;
      this.menuItems[nextIndex].current?.focus();
    }
  };

  private handleRegisterMenuItem = (ref: RefObject<HTMLElement>): void => {
    const unsortedItems = this.menuItems.concat(ref);
    this.menuItems = unsortedItems.sort((a, b) => {
      if (b.current != null && a.current?.compareDocumentPosition(b.current) === 2) {
        return 1;
      } else {
        return -1;
      }
    });
  };

  private handleUnregisterMenuItem = (ref: RefObject<HTMLElement>): void => {
    if (this.menuItems.includes(ref)) {
      this.menuItems.splice(this.menuItems.indexOf(ref));
    }
  };

  private preventMouseBubble = (event: React.MouseEvent<HTMLElement>): void => {
    event.nativeEvent.stopImmediatePropagation();
  };

  public render(): ReactNode {
    const { id, children, style, className, size, status } = this.props;

    return (
      <Panel
        className={classNames(
          OptionMenuClassName,
          `${OptionMenuClassName}--${size}`,
          `${OptionMenuClassName}--${status}`,
          className,
        )}
        style={{ ...this.popoverPosition?.overlay, ...style }}
      >
        <div
          id={id}
          role="menu"
          className={`${OptionMenuClassName}__list`}
          ref={this.menuRef}
          tabIndex={-1}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          onKeyDown={this.handleKeyDown}
          onMouseDown={this.preventMouseBubble}
          onClick={this.preventMouseBubble}
        >
          <OptionMenuItemsContext.Provider
            value={{
              registerMenuItem: this.handleRegisterMenuItem,
              unregisterMenuItem: this.handleUnregisterMenuItem,
              menuRef: this.menuRef,
            }}
          >
            {children}
          </OptionMenuItemsContext.Provider>
        </div>
      </Panel>
    );
  }
}

export default OptionMenu;
